import { useMemo, useCallback } from "react";
import { OnChangeValue, MultiValue } from "react-select";
import type {
  IdValue,
  IdValueSelectProps,
} from "components/Shared/Select/useIdValue";
import useLatestRef from "utils/useLatestRef";

export type SimpleValueProps<
  TValues extends string | number | symbol,
  IsMulti extends boolean = false,
> = IdValueSelectProps<IsMulti, TValues>;

type Parameters<
  TValues extends string | number | symbol,
  TOption,
  IsMulti extends boolean = false,
> = SimpleValueProps<TValues, IsMulti> & {
  getOptionFromValue: (value: TValues) => TOption;
  getValueFromOption: (option: TOption) => TValues;
};

export default function useSimpleValue<
  TValues extends string | number | symbol,
  TOption,
  IsMulti extends boolean = false,
>({
  getOptionFromValue,
  getValueFromOption,
  isMulti,
  onChange,
  value: rawValue,
}: Parameters<TValues, TOption, IsMulti>) {
  const latestGetOptionFromValue = useLatestRef(getOptionFromValue);
  const latestGetValueFromOption = useLatestRef(getValueFromOption);

  const value = useMemo(() => {
    if (rawValue === null) return null;

    if (!rawValue) return;

    if (Array.isArray(rawValue)) {
      return rawValue.map(latestGetOptionFromValue.current);
    }

    return latestGetOptionFromValue.current(rawValue as TValues);
  }, [rawValue, latestGetOptionFromValue]);

  const handleChange = useCallback(
    (newValue: OnChangeValue<TOption, IsMulti>) => {
      if (isMulti) {
        onChange?.(
          (newValue as MultiValue<TOption>).map(
            latestGetValueFromOption.current,
          ) as IdValue<IsMulti, TValues>,
        );
      } else if (!newValue) {
        onChange?.(null as IdValue<IsMulti, TValues>);
      } else {
        onChange?.(
          latestGetValueFromOption.current(newValue as TOption) as IdValue<
            IsMulti,
            TValues
          >,
        );
      }
    },
    [onChange, isMulti, latestGetValueFromOption],
  );

  return { value, handleChange };
}
